import { defineStore } from 'pinia'

import { parseWalletResponse } from '@/modules/wallet/api/parseWalletResponse'
import { loadWalletDashboard, loadWalletHistory, loadExchangeRates, updateAssetsWithRates, normalizeTransaction } from '@/modules/wallet/services/walletService'
import type {
  WalletAsset,
  WalletDashboardData,
  WalletHistoryRequest,
  WalletHistoryState,
  WalletOverview,
  WalletTransaction,
  WalletTransactionApiItem,
  WithdrawalPayload,
  SendFundsPayload,
  ExchangeResponse,
  ExchangePayload,
  ExchangeRateInfo,
} from '@/modules/wallet/types/wallet'

interface WalletState {
  overview: WalletOverview | null
  assets: WalletAsset[]
  recentTransactions: WalletTransaction[]
  dashboardLoading: boolean
  ratesLoading: boolean
  rates: Record<string, number>
  historyLoading: boolean
  error: string | null
  historyError: string | null
  history: WalletHistoryState
  filters: WalletHistoryRequest
  minWithdrawal: Record<string, number>
  feePercent: Record<string, number>
  isBalanceHidden: boolean
}

const createDefaultHistory = (): WalletHistoryState => ({
  items: [],
  page: 1,
  hasMore: false,
})

// HK-030.5-X: in-flight promise держим вне Pinia state, иначе storeToRefs
// попытается развернуть его как ref и упадёт на null.effect (см. WalletDashboardPage).
let ratesInFlight: Promise<void> | null = null

export const useWalletStore = defineStore('wallet', {
  state: (): WalletState => ({
    overview: null,
    assets: [],
    recentTransactions: [],
    dashboardLoading: false,
    ratesLoading: false,
    rates: {},
    historyLoading: false,
    error: null,
    historyError: null,
    history: createDefaultHistory(),
    filters: {
      page: 1,
    },
    minWithdrawal: {},
    feePercent: {},
    isBalanceHidden: localStorage.getItem('wallet:isBalanceHidden') === 'true',
  }),
  actions: {
    async bootstrapDashboard(force = false) {
      if (this.dashboardLoading) {
        return
      }

      if (this.overview && !force) {
        return
      }

      this.dashboardLoading = true
      this.error = null

      try {
        const data: WalletDashboardData = await loadWalletDashboard()
        this.overview = data.overview
        this.assets = data.assets
        this.recentTransactions = data.recentTransactions
        this.minWithdrawal = data.minWithdrawal
        this.feePercent = data.feePercent

        // Load rates asynchronously - don't block dashboard
        this.loadRates()
      } catch (error) {
        console.warn('[GonkaGame][wallet] ошибка загрузки дашборда', error)
        this.error = 'Не удалось загрузить кошелёк'
      } finally {
        this.dashboardLoading = false
      }
    },
    async loadRates() {
      // HK-030.5-X: курсы загружаются один раз и шарятся между параллельными
      // вызывающими (bootstrap fire-and-forget + buy-flow со своим await).
      // Раньше второй concurrent-вызов возвращал undefined по `ratesLoading`
      // флагу — buy-flow не дожидался конвертации USDT→USD и видел totalUsd=0.
      if (ratesInFlight) return ratesInFlight

      ratesInFlight = (async () => {
        this.ratesLoading = true
        try {
          const rates = await loadExchangeRates()
          this.rates = rates

          if (this.assets.length > 0) {
            this.assets = updateAssetsWithRates(this.assets, rates)

            const totalUsd = this.assets.reduce((acc, asset) => acc + (asset.usdValue ?? 0), 0)
            if (this.overview && totalUsd > 0) {
              this.overview = {
                ...this.overview,
                totalUsd: Number(totalUsd.toFixed(2)),
                totalLabel: `$${totalUsd.toFixed(2)}`,
              }
            }
          }
        } catch (error) {
          console.warn('[GonkaGame][wallet] ошибка загрузки курсов', error)
        } finally {
          this.ratesLoading = false
          ratesInFlight = null
        }
      })()

      return ratesInFlight
    },
    async refreshDashboard() {
      await this.bootstrapDashboard(true)
    },
    async loadHistory(page = 1) {
      if (this.historyLoading) {
        return
      }

      this.historyLoading = true
      this.historyError = null
      this.filters.page = page

      try {
        const history = await loadWalletHistory({
          ...this.filters,
          page,
        })
        this.history = history
      } catch (error) {
        console.warn('[GonkaGame][wallet] ошибка загрузки истории', error)
        this.historyError = 'Не удалось загрузить историю транзакций'
      } finally {
        this.historyLoading = false
      }
    },
    updateFilters(partial: Partial<WalletHistoryRequest>) {
      this.filters = {
        ...this.filters,
        ...partial,
        page: 1,
      }
      this.history = createDefaultHistory()
    },
    async requestWithdrawal(payload: WithdrawalPayload) {
      const { postWithdrawalRequest } = await import('@/modules/wallet/api/walletApi')
      try {
        const response = await postWithdrawalRequest(payload)

        if (!response) {
          return { success: false }
        }

        // JSMessage is handled globally by httpClient interceptor.
        // We only care if it was a success for local UI state (close modal).
        if (parseWalletResponse(response).ok) {
          await this.refreshDashboard()
          return { success: true }
        }

        return { success: false }
      } catch (error) {
        console.warn('[GonkaGame][wallet] ошибка вывода средств', error)
        return { success: false }
      }
    },
    toggleBalanceVisibility() {
      this.isBalanceHidden = !this.isBalanceHidden
      localStorage.setItem('wallet:isBalanceHidden', String(this.isBalanceHidden))
    },
    async sendFunds(payload: SendFundsPayload): Promise<{ success: true } | { success: false; error: unknown }> {
      const { postSendFundsRequest } = await import('@/modules/wallet/api/walletApi')
      try {
        const response = await postSendFundsRequest(payload)
        if (!parseWalletResponse(response).ok) {
          return { success: false, error: response }
        }
      } catch (error: unknown) {
        return { success: false, error }
      }

      // The transfer is already confirmed; a dashboard failure must not offer a retry.
      try {
        await this.refreshDashboard()
      } catch {
        this.error = 'Не удалось загрузить кошелёк'
      }
      return { success: true }
    },
    /**
     * Добавить транзакцию в начало списков (для динамического обновления после вывода)
     */
    prependTransaction(apiTransaction: WalletTransactionApiItem) {
      const transaction = normalizeTransaction(apiTransaction)

      // Добавить в начало recentTransactions
      this.recentTransactions = [
        transaction,
        ...this.recentTransactions.filter(t => t.id !== transaction.id)
      ].slice(0, 10)  // Ограничить 10 последними

      // Добавить в начало history если загружена
      if (this.history.items.length > 0) {
        this.history.items = [
          transaction,
          ...this.history.items.filter(t => t.id !== transaction.id)
        ]
      }
    },
    async cancelTransaction(id: number) {
      const { postCancelTransaction } = await import('@/modules/wallet/api/walletApi')
      try {
        await postCancelTransaction(id)
        await Promise.all([this.refreshDashboard(), this.loadHistory(this.filters.page ?? 1)])
        return { success: true }
      } catch (error: any) {
        console.warn('[GonkaGame][wallet] ошибка отмены транзакции', error)
        return { success: false }
      }
    },
    async exchangeCurrency(payload: ExchangePayload) {
      const { postExchangeRequest } = await import('@/modules/wallet/api/walletApi')
      try {
        const response = await postExchangeRequest(payload)

        if (!response) {
          return { success: false }
        }

        const parsed = parseWalletResponse<ExchangeResponse['data']>(response)

        if (parsed.ok) {
          await this.refreshDashboard()
          return { success: true }
        }

        // Прокидываем код ошибки/данные — модалке нужно различать exchange_rate_changed
        return {
          success: false,
          error: parsed.error,
          message: parsed.message,
          // При exchange_rate_changed бэкенд кладёт сюда свежий ExchangeRateInfo
          data: parsed.data as ExchangeRateInfo | undefined,
        }
      } catch (error) {
        console.warn('[GonkaGame][wallet] ошибка обмена валюты', error)
        return { success: false }
      }
    },
  },
})

